import { Box, Center, Text, useMantineTheme } from '@mantine/core'
import * as Sentry from '@sentry/react'
import React, { useCallback, useEffect, useState } from 'react'

import { getDb, IPFS_CACHE_DB_NAME, saveToDb } from '@/plugins/db/Db'

import placeholderImageOnLoad from '../../assets/webp_converted/imageOnload.webp'
import { ipfsDomains, processIpfsDomain } from '../../utils/utils'
import RootButton from '../Buttons/RootButton'
import Loading from '../Loading'
import VideoPlayer from '../VideoPlayer'

const timeoutImageLoad = 4000

interface IpfsImageLoaderProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fullImage?: boolean
  ipfsHash: string
}

const IpfsImageLoader: React.FC<IpfsImageLoaderProps> = ({
  fullImage = false,
  ipfsHash,
  ...props
}) => {
  const theme = useMantineTheme()
  const [imageUrl, setImageUrl] = useState<string>(placeholderImageOnLoad)
  const [isVideo, setIsVideo] = useState<boolean>(false)
  const [currentPrefixIndex, setCurrentPrefixIndex] = useState<number>(0)
  const [loadError, setLoadError] = useState<boolean>(false)
  const [loadingImg, setLoadingImg] = useState<boolean>(false)
  const [retryCount, setRetryCount] = useState<number>(0)

  const handleRetry = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()

      // stop previous event propagation
      e.stopPropagation()

      // Reset prefix index if all prefixes have been exhausted
      setCurrentPrefixIndex(0)

      // Reset error state
      setLoadError(false)

      // Increment retry count
      setRetryCount(retryCount + 1)
    },
    [retryCount]
  )

  const handleError = useCallback(() => {
    setLoadError(true)
  }, [])

  const fetchImage = useCallback(async () => {
    // Get ipfs domain
    const ipfsDomain = ipfsDomains[currentPrefixIndex]

    // Dont fetch if ipfs domain prefix is undefined
    if (ipfsDomain === undefined) {
      return
    }

    // Construct url
    const url = processIpfsDomain(ipfsDomain, ipfsHash)
    setLoadingImg(true)

    // Get IndexedDB instance ipfs-cache
    const db = await getDb(IPFS_CACHE_DB_NAME)
    // check if db has table ipfs
    const hasIpfsTable = !!(await db?.table('ipfs').count())

    // Check if the image is already in the cache
    const cachedResponse = hasIpfsTable && (await db?.table('ipfs').get(ipfsHash))
    if (cachedResponse) {
      // Check if content type is video
      if (cachedResponse.data.type.includes('video')) {
        setIsVideo(true)
      }

      // if data content type is plain text, it means the image is not found. So, remove it from the cache and retry
      if (cachedResponse.data.type.includes('text')) {
        try {
          await db?.table('ipfs').delete(ipfsHash)
        } catch (error) {
          console.error('error', error)
        }
        fetchImage()
        return
      }

      setImageUrl(URL.createObjectURL(await cachedResponse.data))
      setLoadingImg(false)
      return
    }

    // Create a new AbortController for each attempt
    const controller = new AbortController()
    const { signal } = controller

    // Set a timeout for the fetch request
    const startTime = Date.now()
    const timeoutId = setTimeout(
      () => controller.abort(),
      (currentPrefixIndex + 1) * timeoutImageLoad
    )

    try {
      // If not, fetch the image from the IPFS URL
      const response = await fetch(url, { signal })
      if (!response.ok) {
        throw new Error(`Failed to fetch IPFS image: ${response.statusText}`)
      }
      const blob = await response.blob()

      // Check if content type is video
      if (blob.type.includes('video')) {
        setIsVideo(true)
      }

      // Set the image url
      setImageUrl(URL.createObjectURL(blob))

      // Save the image to IndexedDB
      console.log('saving IPFS Image to db', ipfsHash)
      await saveToDb(db, 'ipfs', { cid: ipfsHash, data: blob, timestamp: Date.now() })

      // on success, clear the timer and error state
      clearTimeout(timeoutId)
      setLoadError(false)
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // check time elapsed in seconds
        const elapsedTime = Date.now() - startTime
        console.log('Request was aborted due to timeout after', elapsedTime / 1000, 'seconds')
      } else {
        console.error('Fetch failed:', error)
        Sentry.withScope(scope => {
          scope.setTag('ignore_error', 'true')
        })
      }
      // No need to clear the timeout here, as it’s already cleared if the fetch was aborted

      // Try another prefix
      if (currentPrefixIndex < ipfsDomains.length - 1) {
        setCurrentPrefixIndex(prev => prev + 1)
      } else {
        setLoadError(true)
      }
    }
    setLoadingImg(false)
  }, [currentPrefixIndex, ipfsHash])

  useEffect(() => {
    // Don't fetch if the image has already loaded
    if (imageUrl === placeholderImageOnLoad) {
      fetchImage()
    }
  }, [fetchImage])

  return (
    <>
      {loadingImg && (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: props.width,
            height: props.height,
            alignSelf: 'center',
          }}
          className={props.className}
        >
          <Loading />
        </Box>
      )}
      {!loadingImg &&
        (isVideo ? (
          <VideoPlayer
            fullImage={fullImage}
            className={props.className}
            videoSrc={imageUrl}
            onError={handleError}
            controls
            autoPlay={false}
          />
        ) : (
          <img
            className={props.className}
            src={imageUrl}
            alt={`ipfs_${ipfsHash}`}
            style={{ ...props.style, display: loadError ? 'none' : 'block' }}
            onError={handleError}
          />
        ))}
      {loadError && (
        <Center
          style={{
            borderRadius: '12px',
            backgroundColor: theme.colors.background2[0],
            flexDirection: 'column',
            gap: '20px',
            height: props.height,
            width: props.width,
          }}
        >
          <Text
            style={{
              textAlign: 'center',
              maxWidth: '80%',
            }}
          >
            Failed to load image from IPFS. Please try again.
          </Text>
          <RootButton secondary onClick={handleRetry}>
            Retry
          </RootButton>
        </Center>
      )}
    </>
  )
}

export default IpfsImageLoader
